import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  isAnyRDT,
  isCoworkerHub,
  isCoworkerHubRDT,
  isRDTDevice,
  isRDTDevice11,
} from "@coworker/reusable/helpers/devices";
import { detectBarcodeFormat } from "@ingka-group-digital/flexa-scanner";

const Page = styled.div`
  padding: 1rem;
  font-family: monospace;
  font-size: 13px;
  background: #1a1a1a;
  color: #e0e0e0;
  min-height: 100vh;
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  color: #ffcc00;
  font-size: 14px;
  margin: 0 0 0.5rem;
  border-bottom: 1px solid #444;
  padding-bottom: 4px;
`;

const Row = styled.div<{ highlight?: boolean }>`
  padding: 3px 0;
  color: ${({ highlight }) => (highlight ? "#00ff88" : "#e0e0e0")};
`;

const LogList = styled.div`
  max-height: 50vh;
  overflow-y: auto;
  background: #111;
  border: 1px solid #333;
  padding: 6px;
  border-radius: 4px;
`;

const LogLine = styled.div<{ type?: "scan" | "raw" | "error" }>`
  border-bottom: 1px solid #222;
  padding: 3px 0;
  word-break: break-all;
  color: ${({ type }) =>
    type === "scan" ? "#00ff88" : type === "error" ? "#ff5555" : "#aaa"};
`;

const ClearButton = styled.button`
  background: #333;
  border: 1px solid #555;
  color: #fff;
  padding: 4px 12px;
  cursor: pointer;
  border-radius: 4px;
  font-family: monospace;
  margin-top: 6px;
`;

type LogEntry = {
  time: string;
  type: "scan" | "raw" | "error";
  message: string;
};

export default function ScannerDebugPage() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const logListRef = useRef<HTMLDivElement>(null);

  const addLog = (type: LogEntry["type"], message: string) => {
    const time = new Date().toISOString().slice(11, 23);
    setLogs((prev) => [{ time, type, message }, ...prev].slice(0, 200));
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const raw = JSON.stringify(event.data)?.slice(0, 200) ?? "(empty)";
      addLog("raw", `origin="${event.origin}" data=${raw}`);

      const input = event.data || "";
      const code =
        input?.split && input.split("honeywellbarcode://")[1]?.trim();
      if (code) {
        const format = detectBarcodeFormat(code);
        addLog("scan", `PARSED SCAN → code="${code}" format="${format}"`);
      }
    };

    window.addEventListener("message", handleMessage, false);
    return () => window.removeEventListener("message", handleMessage, false);
  }, []);

  const deviceInfo = [
    { label: "userAgent", value: navigator.userAgent },
    { label: "isCoworkerHub()", value: String(isCoworkerHub()) },
    { label: "isCoworkerHubRDT()", value: String(isCoworkerHubRDT()) },
    { label: "isAnyRDT()", value: String(isAnyRDT()) },
    { label: "isRDTDevice()", value: String(isRDTDevice()) },
    { label: "isRDTDevice11()", value: String(isRDTDevice11()) },
    { label: "window.location.origin", value: window.location.origin },
  ];

  return (
    <Page>
      <Section>
        <SectionTitle>🔍 Scanner Debug Page</SectionTitle>
        <Row style={{ color: "#888", fontSize: 11 }}>
          Open this page, then scan a barcode to see what arrives.
        </Row>
      </Section>

      <Section>
        <SectionTitle>📱 Device Detection</SectionTitle>
        {deviceInfo.map(({ label, value }) => (
          <Row key={label} highlight={value === "true"}>
            <span style={{ color: "#888" }}>{label}: </span>
            {value}
          </Row>
        ))}
      </Section>

      <Section>
        <SectionTitle>
          📡 Message Log ({logs.length})
          <ClearButton onClick={() => setLogs([])}>Clear</ClearButton>
        </SectionTitle>
        <LogList ref={logListRef}>
          {logs.length === 0 && (
            <LogLine type="raw">Waiting for messages...</LogLine>
          )}
          {logs.map((entry, i) => (
            <LogLine key={i} type={entry.type}>
              [{entry.time}] {entry.type.toUpperCase()} — {entry.message}
            </LogLine>
          ))}
        </LogList>
      </Section>
    </Page>
  );
}
